import type { Product, ProductWithPrice } from "../types";

/**
 * Groups products by supplier ID for batch inventory fetching
 */
export function groupProductsBySupplier(products: Product[]): {
    supplierGroups: Map<string, Product[]>;
    productsWithoutSupplier: Product[];
} {
    const supplierGroups = new Map<string, Product[]>();
    const productsWithoutSupplier: Product[] = [];

    for (const product of products) {
        if (product.SupplierId && product.ManufacturerProductId) {
            if (!supplierGroups.has(product.SupplierId)) {
                supplierGroups.set(product.SupplierId, []);
            }
            supplierGroups.get(product.SupplierId)!.push(product);
        } else {
            productsWithoutSupplier.push(product);
        }
    }

    return { supplierGroups, productsWithoutSupplier };
}

/**
 * Fetches inventory prices for all suppliers and creates a price map
 */
async function fetchSupplierPrices(
    supplierGroups: Map<string, Product[]>,
    listInventorySvc: any
): Promise<Map<string, number>> {
    const priceMap = new Map<string, number>(); // Key: SupplierId_ManufacturerProductId, Value: UnitPrice

    for (const [supplierId] of Array.from(supplierGroups)) {
        try {
            const inventory = await listInventorySvc.mutateAsync({ SupplierId: supplierId });

            // Create price map for this supplier
            for (const invItem of inventory) {
                if (invItem.ManufacturerProductId && invItem.UnitPrice) {
                    const key = `${supplierId}_${invItem.ManufacturerProductId}`;
                    priceMap.set(key, Number(invItem.UnitPrice));
                }
            }
        } catch (error) {
            console.error(`Error fetching inventory for supplier ${supplierId}:`, error);
            // Continue with other suppliers even if one fails
        }
    }

    return priceMap;
}

/**
 * Applies pricing information to products based on supplier inventory
 */
function applyPricingToProducts(products: Product[], priceMap: Map<string, number>): ProductWithPrice[] {
    return products.map((product) => {
        let UnitPrice = 0;

        if (product.SupplierId && product.ManufacturerProductId) {
            const key = `${product.SupplierId}_${product.ManufacturerProductId}`;
            UnitPrice = priceMap.get(key) || 0;
        }

        const Total = parseFloat((product.ItemCount * UnitPrice).toFixed(2));
        return { ...product, UnitPrice, Total };
    });
}

/**
 * Adds pricing information to products by fetching supplier inventory
 */
export async function addPricingInfo(products: Product[], listInventorySvc: any): Promise<ProductWithPrice[]> {
    const { supplierGroups } = groupProductsBySupplier(products);
    const priceMap = await fetchSupplierPrices(supplierGroups, listInventorySvc);
    return applyPricingToProducts(products, priceMap);
}
